import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Authenticated user profile mirrored from the backend `/api/v1/users/me`
 * endpoint. Keep this minimal — feature pages can fetch fuller details on
 * demand.
 */
export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role: 'super_admin' | 'org_admin' | 'staff' | 'customer';
  avatar_url?: string | null;
  email_verified?: boolean;
  locale?: string | null;
  theme?: string | null;
  org_ids?: string[];
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;

  /** True when both an access token and a user profile are present. */
  isAuthenticated: () => boolean;

  setSession: (params: {
    accessToken: string;
    refreshToken: string;
    user?: AuthUser | null;
  }) => void;
  setUser: (user: AuthUser | null) => void;
  setAccessToken: (token: string) => void;
  clearSession: () => void;
}

export const AUTH_STORAGE_KEY = 'tgc.auth';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,

      isAuthenticated: () => {
        const { accessToken, user } = get();
        return Boolean(accessToken && user);
      },

      setSession: ({ accessToken, refreshToken, user }) =>
        set((state) => ({
          accessToken,
          refreshToken,
          user: user ?? state.user,
        })),

      setUser: (user) => set({ user }),

      setAccessToken: (accessToken) => set({ accessToken }),

      clearSession: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
        }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      version: 1,
    },
  ),
);

/**
 * Non-React accessor for use inside the Axios interceptor where calling a hook
 * is not possible. Falls through to the persisted store snapshot.
 */
export function getAuthSnapshot() {
  return useAuthStore.getState();
}
